import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import * as WebBrowser from 'expo-web-browser';
import { FileDown } from 'lucide-react-native';

import { Icon } from '@/components/ui/icon';
import { Link, LinkText } from '@/components/ui/link';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import { useAbility } from '@/hooks/useAbility';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { usePrimumAssets } from '@/hooks/usePrimumAssets';
import { type ContentLibraryItem } from '@/types/contentLibraryItems';
import { segmentClient } from '@/util/analytics';

export function ContentLibraryItemDownloadLink({
  contentLibraryItem,
  isPublic,
}: {
  contentLibraryItem: ContentLibraryItem;
  isPublic: boolean;
}) {
  const canReadContentLibrary = useAbility().can('read', 'ContentLibraryItem');

  const { guard } = useAbilityGate();

  const { getCloudFrontUrl } = usePrimumAssets();

  const downloadUrl = contentLibraryItem.downloadableArticleUrl
    ? getCloudFrontUrl(String(contentLibraryItem.downloadableArticleUrl))
    : null;

  if (!downloadUrl) {
    return null;
  }

  const downloadLabelCopy =
    contentLibraryItem.type === 'Webinar' ? 'Download Presentation' : 'Download Article';

  return isWeb ? (
    <Link
      className="flex-row items-center gap-1.5 py-3"
      href={!canReadContentLibrary && !isPublic ? undefined : downloadUrl}
      isExternal
      onPress={(event) => {
        if (!isPublic && !guard('read', 'ContentLibraryItem')) {
          event?.preventDefault();
        } else {
          segmentClient.track(
            ANALYTICS_EVENTS.CONTENT_LIBRARY_ITEM_VIEW_DOWNLOAD_ARTICLE_LINK_CLICKED,
            {
              itemId: contentLibraryItem.id,
            }
          );
        }
      }}>
      <Icon as={FileDown} className="text-primary-500" size="sm" />
      <LinkText className="text-primary-500 hover:text-primary-600">{downloadLabelCopy}</LinkText>
    </Link>
  ) : (
    <Pressable
      className="flex-row items-center gap-1.5 py-3"
      onPress={async (event) => {
        if (!isPublic && !guard('read', 'ContentLibraryItem')) {
          event?.preventDefault();
        } else {
          segmentClient.track(
            ANALYTICS_EVENTS.CONTENT_LIBRARY_ITEM_VIEW_DOWNLOAD_ARTICLE_LINK_CLICKED,
            {
              itemId: contentLibraryItem.id,
            }
          );

          await WebBrowser.openBrowserAsync(downloadUrl);
        }
      }}>
      <Icon as={FileDown} className="text-primary-500" size="sm" />
      <Text className="text-primary-500">{downloadLabelCopy}</Text>
    </Pressable>
  );
}
